import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FaShieldAlt, 
  FaAward, 
  FaUsers, 
  FaLightbulb, 
  FaBullseye, 
  FaEye, 
  FaArrowRight,
  FaMobileAlt
} from 'react-icons/fa';
import './About.css';

const About = () => {
  const logos = [
    {
      name: 'Peace Mobiles',
      tagline: 'Smart Connectivity & Future Innovation',
      src: '/images/logos/peace-logo.png',
      alt: 'Peace Mobiles Logo',
      badge: 'Flagship Brand'
    },
    {
      name: 'Forme Mobiles',
      tagline: 'Inspire My Life • Established 2009',
      src: '/images/logos/forme-logo.png',
      alt: 'Forme Mobiles Logo',
      badge: 'Sister Brand'
    },
    {
      name: 'CYO',
      tagline: 'Create Your Own • Customized Tech',
      src: '/images/logos/cyo-logo.png',
      alt: 'CYO Logo',
      badge: 'Brand Line'
    }
  ];

  const highlights = [
    {
      icon: <FaShieldAlt className="about-hl-icon text-primary" />,
      title: 'A Legacy of Trust',
      desc: "With shared industry roots dating back to 2009, we have a proven track record of understanding mobile technology and anticipating our customers' needs."
    },
    {
      icon: <FaAward className="about-hl-icon text-accent" />,
      title: 'Uncompromising Quality',
      desc: 'We blend premium components with rigorous testing to ensure every device can handle the demands of your daily life.'
    },
    {
      icon: <FaUsers className="about-hl-icon text-info" />,
      title: 'Customer-First Approach',
      desc: 'Our community is at the heart of everything we do. We build phones for you, backed by dedicated support you can rely on.'
    },
    {
      icon: <FaLightbulb className="about-hl-icon text-warning" />,
      title: 'Continuous Innovation',
      desc: "Drawing on our deep shared heritage with Forme Mobiles, the Peace lineup never stops pushing the boundaries of what's possible in mobile tech."
    }
  ];

  return (
    <div className="about-page-root">
      {/* Hero Section */}
      <section className="about-hero-section text-center">
        <Container>
          <span className="about-eyebrow">Our Heritage & Future</span>
          <h1 className="about-main-title">Crafting Tomorrow's Mobile Experience</h1>
          <p className="about-hero-subtitle">
            A shared legacy of engineering excellence, continuous innovation, and human-centric design.
          </p>
        </Container>
      </section>

      {/* 3 Brand Logos Showcase Section */}
      <section className="about-logos-section">
        <Container>
          <div className="about-section-header text-center mb-5">
            <span className="about-badge-pill">Unified Brand Family</span>
            <h2 className="about-section-title">Our Brand Ecosystem</h2>
            <p className="about-section-sub">
              United by shared DNA, precision craftsmanship, and a relentless focus on customer satisfaction.
            </p>
          </div>

          <Row className="g-4 justify-content-center">
            {logos.map((logo, index) => (
              <Col key={index} lg={4} md={6} sm={12}>
                <div className="about-logo-card">
                  <div className="about-logo-badge">{logo.badge}</div>
                  <div className="about-logo-img-wrapper">
                    <img src={logo.src} alt={logo.alt} className="about-logo-img" />
                  </div>
                  <h3 className="about-logo-title">{logo.name}</h3>
                  <p className="about-logo-desc">{logo.tagline}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Our Story Section */}
      <section className="about-story-section">
        <Container>
          <div className="about-story-card">
            <Row className="align-items-center g-5">
              <Col lg={12}>
                <div className="about-story-content">
                  <span className="about-eyebrow">Shared Roots Since 2009</span>
                  <h2 className="about-story-title">Our Story: A Shared Legacy of Mobile Innovation</h2>
                  <div className="about-story-text">
                    <p>
                      Our journey in the mobile industry is rooted in a rich legacy that began in 2009 alongside our sister brand, <strong>Forme Mobiles</strong>. With a shared foundation of putting reliable, accessible mobile technology into the hands of everyday users, <strong>Peace Mobiles</strong> was launched to expand our horizons and focus on the future of smart connectivity.
                    </p>
                    <p>
                      While Peace and Forme operate today as distinct, active brands, they share the exact same core DNA. With well over a decade of industry expertise behind us, we understand what users truly need—durability, smart innovation, and seamless connectivity. Today, we are proud to build on this shared heritage to bring you cutting-edge mobile devices designed specifically for modern life.
                    </p>
                  </div>
                  <div className="about-stats-strip">
                    <div className="about-stat-item">
                      <span className="stat-num">2009</span>
                      <span className="stat-label">Heritage Inception</span>
                    </div>
                    <div className="about-stat-divider" />
                    <div className="about-stat-item">
                      <span className="stat-num">15+</span>
                      <span className="stat-label">Years Expertise</span>
                    </div>
                    <div className="about-stat-divider" />
                    <div className="about-stat-item">
                      <span className="stat-num">600+</span>
                      <span className="stat-label">Service Centers</span>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Mission & Vision Section */}
      <section className="about-mission-section">
        <Container>
          <Row className="g-4">
            <Col md={6}>
              <div className="about-mv-card mission-card">
                <div className="about-mv-icon-box">
                  <FaBullseye className="about-mv-icon" />
                </div>
                <h3 className="about-mv-title">Our Mission</h3>
                <p className="about-mv-text">
                  To empower individuals by delivering high-quality, innovative, and accessible mobile technology that bridges the gap between people and their potential.
                </p>
              </div>
            </Col>

            <Col md={6}>
              <div className="about-mv-card vision-card">
                <div className="about-mv-icon-box">
                  <FaEye className="about-mv-icon" />
                </div>
                <h3 className="about-mv-title">Our Vision</h3>
                <p className="about-mv-text">
                  To be a globally trusted mobile brand recognized for our reliability, forward-thinking design, and an unwavering commitment to our customers.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why Choose Us Section */}
      <section className="about-why-section">
        <Container>
          <div className="about-section-header text-center mb-5">
            <span className="about-badge-pill">The Peace Promise</span>
            <h2 className="about-section-title">Why Choose Us?</h2>
            <p className="about-section-sub">
              Engineering with integrity, designed for endurance, backed by unwavering support.
            </p>
          </div>

          <Row className="g-4">
            {highlights.map((item, idx) => (
              <Col key={idx} lg={6} md={6}>
                <div className="about-why-card">
                  <div className="about-why-icon-wrap">
                    {item.icon}
                  </div>
                  <div className="about-why-body">
                    <h4 className="about-why-item-title">{item.title}</h4>
                    <p className="about-why-item-desc">{item.desc}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* CTA Banner */}
          <div className="about-cta-banner mt-5">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
              <div>
                <h3 className="mb-1 text-white">Explore Our Latest Mobile Lineup</h3>
                <p className="mb-0 text-secondary">
                  Discover smartphones crafted for power, precision, and everyday elegance.
                </p>
              </div>
              <div className="d-flex gap-3 flex-wrap">
                <Link to="/products" className="apple-btn apple-btn-primary whitespace-nowrap">
                  <FaMobileAlt className="me-2" /> View Products
                </Link>
                <Link to="/contact" className="apple-btn apple-btn-secondary whitespace-nowrap">
                  Contact Us <FaArrowRight className="ms-2" />
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default About;
